import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { ApiResponse } from "apisauce";

import { useAppDispatch, LargeBatchThreshold } from "../../hooks";
// Decoded from the JWT rather than the Redux store, so a hard refresh of /settings
// still knows the role (the store is only populated on login).
import { getCurrentUser } from "../../services/authService";
import { setSetting } from "../../store/settingStore";
import settingService from "../../services/settingService";
import { Setting } from "../../interface/api/Setting";
import { isAdmin } from "../../utils/constants";
import PageHero from "../shared/page-hero";

const useStyles = makeStyles({
  section: { padding: "20px", marginBottom: "16px" },
  sectionTitle: { fontWeight: 600, marginBottom: "4px" },
  sectionHint: { marginBottom: "16px" },
});

// Everything in the Settings row was previously editable only by hand-written SQL.
// Administrator-only; the PUT is guarded server-side as well.
const SettingsView = ({}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const user = getCurrentUser();

  const [setting, setLocalSetting] = useState<Setting | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingService.getSetting().then((response) => {
      if (response.ok) {
        setLocalSetting(response.data as Setting);
      } else {
        toast.error("Failed to load settings");
      }
    });
  }, []);

  const update = (field: keyof Setting, value: string | number) => {
    if (!setting) return;
    setLocalSetting({ ...setting, [field]: value });
  };

  const numberField = (
    field: keyof Setting,
    label: string,
    helperText?: string
  ) => (
    <Grid item xs={12} sm={6} md={4}>
      <TextField
        fullWidth
        variant="outlined"
        type="number"
        label={label}
        helperText={helperText}
        value={setting?.[field] ?? ""}
        onChange={(e) =>
          update(field, e.target.value === "" ? 0 : Number(e.target.value))
        }
      />
    </Grid>
  );

  const textField = (field: keyof Setting, label: string) => (
    <Grid item xs={12} sm={6} md={4}>
      <TextField
        fullWidth
        variant="outlined"
        label={label}
        value={setting?.[field] ?? ""}
        onChange={(e) => update(field, e.target.value)}
      />
    </Grid>
  );

  const section = (
    title: string,
    hint: string,
    fields: React.ReactNode
  ) => (
    <Paper variant="outlined" className={classes.section}>
      <Typography variant="h6" className={classes.sectionTitle}>
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        className={classes.sectionHint}
      >
        {hint}
      </Typography>
      <Divider style={{ marginBottom: 20 }} />
      <Grid container spacing={3}>
        {fields}
      </Grid>
    </Paper>
  );

  const onSave = async () => {
    if (!setting) return;

    if (setting.ticketGenerateMaxRange <= 0 || setting.ticketExportMaxRange <= 0) {
      toast.error("Batch limits must be greater than zero");
      return;
    }

    setSaving(true);
    const response: ApiResponse<any> = await settingService.updateSetting(setting);
    if (response.ok) {
      const saved = response.data as Setting;
      setLocalSetting(saved);
      // Keep the Redux copy in step — the generate/export forms read their limits from it.
      dispatch(setSetting({ setting: saved }));
      toast.success("Settings saved");
    } else {
      toast.error((response.data as string) || "Failed to save settings");
    }
    setSaving(false);
  };

  if (!user || !isAdmin(user.role)) {
    return (
      <Container maxWidth="xl">
        <PageHero title="Settings" subtitle="" showBackButton={false} />
        <Typography>You do not have permission to view this page.</Typography>
      </Container>
    );
  }

  if (!setting) {
    return (
      <Container maxWidth="xl">
        <PageHero title="Settings" subtitle="" showBackButton={false} />
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <PageHero
        title="Settings"
        subtitle="Portal-wide configuration. Changes take effect immediately."
        showBackButton={false}
      />

      {section(
        "Ticket Numbering",
        "The prefix each stream stamps on newly generated tickets. Changing a prefix starts a new number space for that stream.",
        <>
          {numberField("currentPrefix", "Main Pit prefix")}
          {numberField("smallQuantityCurrentPrefix", "Small Quantity prefix")}
          {numberField("rubbleCurrentPrefix", "Rubble prefix")}
          {numberField("hydrovacCurrentPrefix", "Hydrovac prefix")}
        </>
      )}

      {section(
        "Batch Limits",
        "Guards against a mistyped range. Generating or exporting 10,000 tickets in one batch is expected — these only stop something like 1 to 99,999,999.",
        <>
          {numberField(
            "ticketGenerateMaxRange",
            "Max tickets per generate",
            `Batches above ${LargeBatchThreshold.toLocaleString()} ask for confirmation.`
          )}
          {numberField(
            "ticketExportMaxRange",
            "Max tickets per export",
            "Exports are emailed when ready."
          )}
        </>
      )}

      {section(
        "Haulage Record Contact",
        "The receiving-site contact printed on every haulage record. This is the only part of Settings visible to anyone scanning a ticket.",
        <>
          {textField("haulageRecordReceiverName", "Name")}
          {textField("haulageRecordReceiverPhone", "Phone")}
          {textField("haulageRecordReceiverEmail", "Email")}
        </>
      )}

      {section(
        "Royalties",
        "Used by the CPI-adjusted royalty report. Partner rates are configured separately.",
        <>
          {numberField("royaltyBaseRate", "Base rate")}
          {numberField("royaltyBackgroundCpi", "Background CPI")}
          {numberField("royaltyTownBaseRate", "Town base rate")}
          {numberField(
            "royaltyNetTicketPrice",
            "Net ticket price ($)",
            "Hydrovac and Small Quantities royalties use this instead of the sale price"
          )}
        </>
      )}

      {section(
        "Scale",
        "Default applied to a truck with no recorded tare weight.",
        <>{numberField("defaultTareWeight", "Default tare weight (kg)")}</>
      )}

      {/* MUI's Button directly, not the shared one — that hardcodes fullWidth, which
          stretched Save across the whole page. */}
      <Box display="flex" justifyContent="flex-end" mb={4}>
        <Button
          type="button"
          color="primary"
          variant="contained"
          onClick={onSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Container>
  );
};

export default connect()(SettingsView);
